import { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { PackageSearch } from 'ts/package/interfaces/Package';
import { useTranslation } from 'react-i18next';
import { getCurrency, formatPriceCurrency, showWarning } from 'utils/utils';
import { dateFormat } from 'utils/shared/utils';
import ProposalHeart from 'components/shared/Proposal/Create/ProposalHeart';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import { getBoardName, getDealType } from 'utils/package';
import TicketBaggagePac from '../Book/TicketBaggagePac';
import InfiniteScroll from 'react-infinite-scroller';
import { useLocation } from 'react-router-dom';
import { imgDefaultBg, isNetravel, isTT } from 'constants/generic';
interface PackagesListProps {
  packages: PackageSearch[];
  onClickChoose: (pac: PackageSearch) => void;
}

const PackagesList: FC<PackagesListProps> = (props) => {
  
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [t] = useTranslation();
  const count = 20;
  const [total, setTotal] = useState(count);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const lastScrollPosition = useRef(0);
  const location = useLocation();

  useEffect(() => {
    setHasMore(props.packages.length > count);
    setTotal(count);
  }, [props.packages?.length]);

  const fetchData = useCallback(() => {
    if (isLoading || !hasMore) return;

    const currentScroll = window.scrollY;
    if (Math.abs(currentScroll - lastScrollPosition.current) < 100) return;

    setIsLoading(true);
    lastScrollPosition.current = currentScroll;

    setTimeout(() => {
      setTotal((prevTotal) => {
        const newTotal = prevTotal + count;
        setHasMore(newTotal < props.packages.length);
        return newTotal;
      });
      setIsLoading(false);
    }, 300);
  }, [isLoading, hasMore, count, props.packages.length]);

  const onImageLoadError = ({ currentTarget }: SyntheticEvent<HTMLImageElement, Event>) => {
    currentTarget.onerror = null;
    currentTarget.src = imgDefaultBg;
  };

  const onClickHeart = () => {
    showWarning(t('package.you_need_to_choose_room_first'));
  };

  return (
    <div className="package-list results-list">
      <InfiniteScroll
        loadMore={fetchData}
        hasMore={hasMore}
        useWindow={true}
        className="flex-one"
        threshold={100}
        loader={<div key={0}></div>}
      >
        {props.packages.slice(0, total).map((pac) => (
          <div key={pac.uniqueKey} className="package-item default-shadow position-relative">
            <ProposalHeart onClickPropsal={onClickHeart} active={false} />
            <div className="package-item-img cur-poi" onClick={() => props.onClickChoose(pac)}>
              <img src={pac.dealImage} alt="pac" onError={onImageLoadError} />
              <span className="package-item-type text-cap">
                {t(getDealType(pac.dealType, 1, location.pathname)?.toLowerCase())}
              </span>
            </div>
            <div className="package-item-data">
              <span className="name text-cap">{pac.rowName?.toLowerCase()}</span>
              {pac.hotelRating ? <Rating value={pac.hotelRating} /> : <div className="mb-10" />}
              <span className="item">
                <span className="display-ib">{t('package.depart')}</span>
                <b>{dateFormat(pac.departureDate, 'EEE - d MMMM, yyyy - HH:mm')}</b>
              </span>
              <span className="item">
                <span className="display-ib">{t('package.return')}</span>
                <b>{dateFormat(pac.returnDate, 'EEE - d MMMM, yyyy - HH:mm')}</b>
              </span>
              <span className="item">
                <span className="display-ib">{t('hotel.nights_up')}</span> <b>{pac.dealLength}</b>
              </span>
              <span className="item">
                <span className="display-ib">{t('package.room_type')}</span>{' '}
                <b className="text-cap">{pac.roomType?.toLowerCase()}</b>
              </span>
              <span className="item">
                <span className="display-ib">{t('filter.board')}</span>{' '}
                <b>{getBoardName(pac.boardingDescription)}</b>
              </span>
              <div className="display-flex">
                {!!pac.provider && !isNetravel && !isTT && (
                  <span className="item mr-10">
                    {t('package.provider')}: <b>{pac.provider}</b>
                  </span>
                )}
                <TicketBaggagePac dealBaggage={pac?.dealBaggage} shortMode={true} />
              </div>
            </div>
            <div className="package-item-total">
              {pac.fastOK && (
                <span className="mt-20 text-center text-success">
                  <FontAwesomeIcon icon={faCheck} /> <b>{t('package.immediate_approval')}</b>
                </span>
              )}
              <div className="price-per-person">
                <span>{t('package.price_per_person')}:</span>
                <span className="price">
                  {formatPriceCurrency(pac.salePrice, getCurrency(pac.currency))}
                </span>
              </div>
              <div className="total-price">
                {t('package.total_price')}:
                <span className="price">
                  {formatPriceCurrency(
                    pac.salePrice * pac.paxComplect.length,
                    getCurrency(pac.currency)
                  )}
                </span>
              </div>
              {isNetravel && (
                <Button title={'הזמן עכשיו'} onClick={() => props.onClickChoose(pac)} />
              )}
              {isTT && <Button title={t('continue')} onClick={() => props.onClickChoose(pac)} />}
              {!isNetravel && !isTT && (
                <FontAwesomeIcon
                  className="cur-poi"
                  icon={faCircleArrowRight}
                  color={colorContrast}
                  size="xl"
                  onClick={() => props.onClickChoose(pac)}
                />
              )}
            </div>
          </div>
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default withErrorBoundary(PackagesList, 'PackagesList');
